import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Box, Typography, Button, Card, CardContent, CardActions,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Grid, Chip, LinearProgress,
  Accordion, AccordionSummary, AccordionDetails,
  Table, TableHead, TableRow, TableCell,
  IconButton, Tooltip, Divider, Alert,
  FormControlLabel, Checkbox, Stack, Badge, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import TableBody from '../components/PagedTableBody'; // pages rows beyond 10
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import PersonIcon from '@mui/icons-material/Person';
import CheckIcon from '@mui/icons-material/Check';
import EventIcon from '@mui/icons-material/Event';

import {
  MY_CEREMONIES_QUERY,
  CEREMONY_TEMPLATES_QUERY,
  CREATE_CEREMONY_MUTATION,
  CREATE_CEREMONY_FROM_PACKAGE_MUTATION,
  FAMILY_VENDOR_OFFERINGS_QUERY,
  UPDATE_CEREMONY_MUTATION,
  DELETE_CEREMONY_MUTATION,
  ADD_RITUAL_TASK_MUTATION,
  TOGGLE_RITUAL_TASK_MUTATION,
  DELETE_RITUAL_TASK_MUTATION,
  ADD_RITUAL_PERSONNEL_MUTATION,
  UPDATE_RITUAL_PERSONNEL_MUTATION,
  DELETE_RITUAL_PERSONNEL_MUTATION,
  CEREMONY_MATERIALS_QUERY,
  ADD_CEREMONY_MATERIAL_MUTATION,
  UPDATE_CEREMONY_MATERIAL_MUTATION,
  DELETE_CEREMONY_MATERIAL_MUTATION,
} from '../graphql/queries';
import { useLov } from '../hooks/useLov';

import Paged from '../components/Paged'; // lists beyond 10 are paged
const CATEGORY_COLORS = {
  material:   'warning',
  personnel:  'info',
  decoration: 'secondary',
  logistics:  'default',
};

const STATUS_COLORS = {
  planned:     'default',
  in_progress: 'primary',
  completed:   'success',
  cancelled:   'error',
};

// ── Task Progress Bar ────────────────────────────────────────
function TaskProgress({ tasks }) {
  const total = tasks.length;
  const done  = tasks.filter(t => t.isDone).length;
  const pct   = total ? Math.round((done / total) * 100) : 0;
  return (
    <Box sx={{ mt: 1 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="caption" color="text.secondary">{done}/{total} tasks done</Typography>
        <Typography variant="caption" color="text.secondary">{pct}%</Typography>
      </Stack>
      <LinearProgress variant="determinate" value={pct} sx={{ height: 6, borderRadius: 3 }} color={pct === 100 ? 'success' : 'primary'} />
    </Box>
  );
}

// ── Personnel Panel ───────────────────────────────────────────
function PersonnelPanel({ ceremony, onRefetch }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ role: '', name: '', contact: '', notes: '' });
  const { options: roleOptions } = useLov('personnel_role');

  const [addPersonnel] = useMutation(ADD_RITUAL_PERSONNEL_MUTATION, {
    onCompleted: () => { setOpen(false); setForm({ role: '', name: '', contact: '', notes: '' }); onRefetch(); },
  });
  const [updatePersonnel] = useMutation(UPDATE_RITUAL_PERSONNEL_MUTATION, { onCompleted: onRefetch });
  const [deletePersonnel] = useMutation(DELETE_RITUAL_PERSONNEL_MUTATION, { onCompleted: onRefetch });

  const handleAdd = () => {
    if (!form.role) return;
    addPersonnel({ variables: { ceremonyId: ceremony.id, ...form } });
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2">Personnel ({ceremony.personnel?.length || 0})</Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={() => setOpen(true)}>Add</Button>
      </Stack>

      {ceremony.personnel?.length > 0 ? (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Role</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>Confirmed</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {ceremony.personnel.map(p => (
              <TableRow key={p.id}>
                <TableCell><Chip label={p.role} size="small" variant="outlined" /></TableCell>
                <TableCell>{p.name || '—'}</TableCell>
                <TableCell>{p.contact || '—'}</TableCell>
                <TableCell>
                  <IconButton size="small" color={p.confirmed ? 'success' : 'default'}
                    onClick={() => updatePersonnel({ variables: { id: p.id, confirmed: !p.confirmed } })}>
                    {p.confirmed ? <CheckCircleIcon fontSize="small" /> : <RadioButtonUncheckedIcon fontSize="small" />}
                  </IconButton>
                </TableCell>
                <TableCell>
                  <IconButton size="small" color="error"
                    onClick={() => deletePersonnel({ variables: { id: p.id } })}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Typography variant="body2" color="text.secondary">No personnel assigned yet.</Typography>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Personnel</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField select label="Role" value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))} fullWidth required>
              {roleOptions.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
            </TextField>
            <TextField label="Name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} fullWidth />
            <TextField label="Contact / Phone" value={form.contact} onChange={e => setForm(f => ({ ...f, contact: e.target.value }))} fullWidth />
            <TextField label="Notes" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} fullWidth multiline rows={2} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAdd} disabled={!form.role}>Add</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Task Panel ────────────────────────────────────────────────
function TaskPanel({ ceremony, onRefetch }) {
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ taskName: '', category: '', assignedTo: '', dueTime: '', notes: '' });
  const { options: catOptions } = useLov('task_category');

  const [addTask] = useMutation(ADD_RITUAL_TASK_MUTATION, {
    onCompleted: () => { setAddOpen(false); setForm({ taskName: '', category: '', assignedTo: '', dueTime: '', notes: '' }); onRefetch(); },
  });
  const [toggleTask] = useMutation(TOGGLE_RITUAL_TASK_MUTATION, { onCompleted: onRefetch });
  const [deleteTask] = useMutation(DELETE_RITUAL_TASK_MUTATION, { onCompleted: onRefetch });

  const grouped = {};
  (ceremony.tasks || []).forEach(t => {
    const cat = t.category || 'other';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(t);
  });

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2">Tasks</Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>Add Task</Button>
      </Stack>

      <TaskProgress tasks={ceremony.tasks || []} />

      {Object.entries(grouped).map(([cat, tasks]) => (
        <Box key={cat} sx={{ mt: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
            <Chip label={cat} size="small" color={CATEGORY_COLORS[cat] || 'default'} />
          </Stack>
          {tasks.map(t => (
            <Stack key={t.id} direction="row" alignItems="center" spacing={1} sx={{ py: 0.5 }}>
              <IconButton size="small" onClick={() => toggleTask({ variables: { id: t.id } })}
                color={t.isDone ? 'success' : 'default'}>
                {t.isDone ? <CheckCircleIcon fontSize="small" /> : <RadioButtonUncheckedIcon fontSize="small" />}
              </IconButton>
              <Typography variant="body2" sx={{ flex: 1, textDecoration: t.isDone ? 'line-through' : 'none', color: t.isDone ? 'text.secondary' : 'text.primary' }}>
                {t.taskName}
              </Typography>
              {t.assignedTo && <Typography variant="caption" color="text.secondary">{t.assignedTo}</Typography>}
              {t.dueTime && <Chip label={t.dueTime} size="small" variant="outlined" />}
              <IconButton size="small" color="error" onClick={() => deleteTask({ variables: { id: t.id } })}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
        </Box>
      ))}

      {(ceremony.tasks?.length === 0) && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>No tasks yet.</Typography>
      )}

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Ritual Task</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Task Name" value={form.taskName} onChange={e => setForm(f => ({ ...f, taskName: e.target.value }))} fullWidth required />
            <TextField select label="Category" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} fullWidth>
              <MenuItem value="">— None —</MenuItem>
              {catOptions.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
            </TextField>
            <TextField label="Assigned To" value={form.assignedTo} onChange={e => setForm(f => ({ ...f, assignedTo: e.target.value }))} fullWidth />
            <TextField label="Due Time / Note" value={form.dueTime} onChange={e => setForm(f => ({ ...f, dueTime: e.target.value }))} fullWidth placeholder="e.g. D-3 09:00" />
            <TextField label="Notes" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} fullWidth multiline rows={2} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => addTask({ variables: { ceremonyId: ceremony.id, ...form } })} disabled={!form.taskName}>Add</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Materials & Decoration Panel ──────────────────────────────
const MAT_CATEGORY_COLORS = { material: 'warning', decoration: 'secondary', ingredient: 'success', equipment: 'info' };
const MAT_STATUS_COLORS   = { pending: 'default', ordered: 'primary', received: 'success', not_required: 'error' };

function MaterialsPanel({ ceremony, onRefetch }) {
  const [open, setOpen]   = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm]   = useState({ itemName: '', category: 'material', quantity: '', unit: '', estimatedCost: '', vendorName: '', notes: '' });
  const { options: catOpts } = useLov('material_category');
  const { options: statusOpts } = useLov('material_status');

  const { data, refetch } = useQuery(CEREMONY_MATERIALS_QUERY, {
    variables: { ceremonyId: ceremony.id }, fetchPolicy: 'cache-and-network',
  });
  const [addMaterial]    = useMutation(ADD_CEREMONY_MATERIAL_MUTATION,    { onCompleted: () => { setOpen(false); setForm({ itemName: '', category: 'material', quantity: '', unit: '', estimatedCost: '', vendorName: '', notes: '' }); refetch(); onRefetch(); } });
  const [updateMaterial] = useMutation(UPDATE_CEREMONY_MATERIAL_MUTATION, { onCompleted: () => { setEditItem(null); refetch(); } });
  const [deleteMaterial] = useMutation(DELETE_CEREMONY_MATERIAL_MUTATION, { onCompleted: refetch });

  const materials = data?.ceremonyMaterials || [];

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2">Materials & Decorations ({materials.length})</Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={() => setOpen(true)}>Add</Button>
      </Stack>

      {materials.length > 0 ? (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Item</TableCell><TableCell>Category</TableCell>
              <TableCell>Qty</TableCell><TableCell>Est. Cost</TableCell>
              <TableCell>Status</TableCell><TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {materials.map(m => (
              <TableRow key={m.id}>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>{m.itemName}</Typography>
                  {m.vendorName && <Typography variant="caption" color="text.secondary">{m.vendorName}</Typography>}
                </TableCell>
                <TableCell><Chip label={m.category} size="small" color={MAT_CATEGORY_COLORS[m.category] || 'default'} variant="outlined" /></TableCell>
                <TableCell>{m.quantity || '—'}</TableCell>
                <TableCell>{m.estimatedCost ? `₹${Number(m.estimatedCost).toLocaleString('en-IN')}` : '—'}</TableCell>
                <TableCell>
                  <Chip label={m.status} size="small" color={MAT_STATUS_COLORS[m.status] || 'default'} />
                </TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => { setEditItem(m); }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error"
                    onClick={() => deleteMaterial({ variables: { id: m.id } })}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Typography variant="body2" color="text.secondary">No materials tracked yet.</Typography>
      )}

      {/* Add Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Material / Decoration</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Item Name" value={form.itemName} onChange={e => setForm(f => ({ ...f, itemName: e.target.value }))} fullWidth required />
            <TextField select label="Category" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} fullWidth>
              {catOpts.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
            </TextField>
            <Stack direction="row" spacing={1}>
              <TextField label="Quantity" value={form.quantity} onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))} fullWidth size="small" placeholder="e.g. 50" />
              <TextField label="Unit" value={form.unit} onChange={e => setForm(f => ({ ...f, unit: e.target.value }))} sx={{ width: 110 }} size="small" placeholder="pieces" />
            </Stack>
            <TextField label="Estimated Cost (₹)" type="number" value={form.estimatedCost} onChange={e => setForm(f => ({ ...f, estimatedCost: e.target.value }))} fullWidth />
            <TextField label="Vendor / Supplier" value={form.vendorName} onChange={e => setForm(f => ({ ...f, vendorName: e.target.value }))} fullWidth />
            <TextField label="Notes" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} fullWidth multiline rows={2} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained"
            onClick={() => addMaterial({ variables: { ceremonyId: ceremony.id, ...form, estimatedCost: form.estimatedCost ? parseFloat(form.estimatedCost) : null } })}
            disabled={!form.itemName}>Add</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      {editItem && (
        <Dialog open={Boolean(editItem)} onClose={() => setEditItem(null)} maxWidth="xs" fullWidth>
          <DialogTitle>Update Material</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField select label="Status" value={editItem.status || 'pending'}
                onChange={e => setEditItem(ei => ({ ...ei, status: e.target.value }))} fullWidth>
                {statusOpts.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
              </TextField>
              <TextField label="Actual Cost (₹)" type="number" value={editItem.actualCost || ''}
                onChange={e => setEditItem(ei => ({ ...ei, actualCost: e.target.value }))} fullWidth />
              <TextField label="Notes" value={editItem.notes || ''}
                onChange={e => setEditItem(ei => ({ ...ei, notes: e.target.value }))} fullWidth multiline rows={2} />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditItem(null)}>Cancel</Button>
            <Button variant="contained"
              onClick={() => updateMaterial({ variables: { id: editItem.id, status: editItem.status, actualCost: editItem.actualCost ? parseFloat(editItem.actualCost) : null, notes: editItem.notes || null } })}>
              Save</Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
}

// ── Ceremony Card ─────────────────────────────────────────────
function CeremonyCard({ ceremony, onRefetch, onEdit, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const done  = (ceremony.tasks || []).filter(t => t.isDone).length;
  const confirmed = (ceremony.personnel || []).filter(p => p.confirmed).length;

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="h6">{ceremony.name}</Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
              <Chip label={ceremony.ceremonyType} size="small" variant="outlined" color="primary" />
              {ceremony.sourceVendor && <Chip label={`Items from ${ceremony.sourceVendor}`} size="small" color="secondary" variant="outlined" />}
              <Chip label={ceremony.status} size="small" color={STATUS_COLORS[ceremony.status] || 'default'} />
              {ceremony.eventDate && (
                <Chip icon={<EventIcon />} label={new Date(ceremony.eventDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })} size="small" variant="outlined" />
              )}
              {ceremony.venue && (
                <Chip label={ceremony.venue} size="small" variant="outlined" />
              )}
            </Stack>
            <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary">{done}/{ceremony.tasks?.length || 0} tasks</Typography>
              <Typography variant="caption" color="text.secondary">
                <PersonIcon sx={{ fontSize: 12, mr: 0.3 }} />{confirmed}/{ceremony.personnel?.length || 0} confirmed
              </Typography>
            </Stack>
            <TaskProgress tasks={ceremony.tasks || []} />
          </Box>
          <Stack direction="row">
            <IconButton size="small" onClick={() => onEdit(ceremony)}><EditIcon fontSize="small" /></IconButton>
            <IconButton size="small" color="error" onClick={() => onDelete(ceremony.id)}><DeleteIcon fontSize="small" /></IconButton>
          </Stack>
        </Stack>
      </CardContent>

      <CardActions sx={{ px: 2, pt: 0 }}>
        <Button size="small" onClick={() => setExpanded(e => !e)} endIcon={<ExpandMoreIcon sx={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: '0.2s' }} />}>
          {expanded ? 'Hide Details' : 'Show Tasks, Personnel & Materials'}
        </Button>
      </CardActions>

      {expanded && (
        <Box sx={{ px: 2, pb: 2 }}>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TaskPanel ceremony={ceremony} onRefetch={onRefetch} />
            </Grid>
            <Grid item xs={12} md={3}>
              <PersonnelPanel ceremony={ceremony} onRefetch={onRefetch} />
            </Grid>
            <Grid item xs={12} md={3}>
              <MaterialsPanel ceremony={ceremony} onRefetch={onRefetch} />
            </Grid>
          </Grid>
        </Box>
      )}
    </Card>
  );
}

// ── Main Page ─────────────────────────────────────────────────
export default function RitualPlanningPage() {
  const { data, loading, error, refetch } = useQuery(MY_CEREMONIES_QUERY, { fetchPolicy: 'cache-and-network' });
  const { data: tmplData } = useQuery(CEREMONY_TEMPLATES_QUERY);
  const { options: statusOptions } = useLov('ceremony_status');
  const navigate = useNavigate();
  // Ceremonies offered by the family's contracted rituals vendor(s): one option per ceremony package
  const { data: ritualData } = useQuery(FAMILY_VENDOR_OFFERINGS_QUERY, { variables: { category: 'rituals' }, fetchPolicy: 'cache-and-network' });
  const vendorCeremonies = (ritualData?.vendorOfferings || []).flatMap(o => (o.packages || []).map(p => ({
    key: `${o.id}|${p.id}`, offeringId: o.id, packageId: p.id, vendor: o.businessName, pkg: p,
  })));
  const [mode, setMode] = useState('vendor'); // vendor | standard
  const [vendorChoice, setVendorChoice] = useState('');
  const chosen = vendorCeremonies.find(v => v.key === vendorChoice);
  const useVendor = mode === 'vendor' && vendorCeremonies.length > 0;
  const inr = (n) => `₹${Number(n).toLocaleString('en-IN')}`;

  const emptyForm = { name: '', ceremonyType: '', eventDate: '', venue: '', notes: '', status: 'planned' };
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen]     = useState(false);
  const [form, setForm]             = useState(emptyForm);
  const [editId, setEditId]         = useState(null);

  const [createCeremony] = useMutation(CREATE_CEREMONY_MUTATION, {
    onCompleted: () => { setCreateOpen(false); setForm(emptyForm); refetch(); },
  });
  const [createFromPackage, { error: fromPkgError }] = useMutation(CREATE_CEREMONY_FROM_PACKAGE_MUTATION, {
    onCompleted: () => { setCreateOpen(false); setForm(emptyForm); setVendorChoice(''); refetch(); },
  });
  const handleCreate = () => {
    const extra = { eventDate: form.eventDate || null, venue: form.venue || null, notes: form.notes || null };
    if (useVendor) createFromPackage({ variables: { offeringId: chosen.offeringId, packageId: chosen.packageId, name: form.name || null, ...extra } });
    else createCeremony({ variables: { ...form, ...extra } });
  };
  const [updateCeremony] = useMutation(UPDATE_CEREMONY_MUTATION, {
    onCompleted: () => { setEditOpen(false); refetch(); },
  });
  const [deleteCeremony] = useMutation(DELETE_CEREMONY_MUTATION, { onCompleted: refetch });

  const templates = tmplData?.ceremonyTemplates || [];
  const ceremonies = data?.myCeremonies || [];

  const handleTypeSelect = (type) => {
    const tmpl = templates.find(t => t.ceremonyType === type);
    setForm(f => ({ ...f, ceremonyType: type, name: tmpl ? tmpl.displayName : f.name }));
  };

  const handleEdit = (c) => {
    setForm({ name: c.name, ceremonyType: c.ceremonyType, eventDate: c.eventDate || '', venue: c.venue || '', notes: c.notes || '', status: c.status });
    setEditId(c.id);
    setEditOpen(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('Delete this ceremony and all its tasks?')) {
      deleteCeremony({ variables: { id } });
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Ritual Planning</Typography>
          <Typography variant="body2" color="text.secondary">Plan and track all ceremonies, tasks, and personnel</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          Add Ceremony
        </Button>
      </Stack>

      {loading && <LinearProgress />}
      {error   && <Alert severity="error" sx={{ mb: 2 }}>{error.message}</Alert>}

      {!loading && ceremonies.length === 0 && (
        <Card sx={{ textAlign: 'center', py: 6 }}>
          <EventIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">No ceremonies planned yet</Typography>
          <Typography variant="body2" color="text.disabled" sx={{ mb: 2 }}>Start planning your wedding ceremonies</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
            Add First Ceremony
          </Button>
        </Card>
      )}

      <Paged items={ceremonies}>{rows => rows.map(c => (
        <CeremonyCard key={c.id} ceremony={c} onRefetch={refetch} onEdit={handleEdit} onDelete={handleDelete} />
      ))}</Paged>

      {/* Create Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Plan a New Ceremony</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <ToggleButtonGroup exclusive size="small" color="primary" value={useVendor ? 'vendor' : 'standard'}
              onChange={(_, v) => v && setMode(v)}>
              <ToggleButton value="vendor" disabled={!vendorCeremonies.length}>From your ritual vendor</ToggleButton>
              <ToggleButton value="standard">Standard ceremony</ToggleButton>
            </ToggleButtonGroup>
            {!vendorCeremonies.length && (
              <Alert severity="info" action={<Button color="inherit" size="small" onClick={() => navigate('/dashboard/service-providers')}>Service Providers</Button>}>
                Your ritual vendor's ceremonies, with their items and costs, appear here once rituals are on a signed contract.
                You can still plan a standard ceremony.
              </Alert>
            )}
            {useVendor ? (
              <TextField select label="Ceremony" value={vendorChoice} fullWidth required
                onChange={e => {
                  const v = vendorCeremonies.find(x => x.key === e.target.value);
                  setVendorChoice(e.target.value);
                  setForm(f => ({ ...f, name: v?.pkg.name || f.name }));
                }}>
                {vendorCeremonies.map(v => (
                  <MenuItem key={v.key} value={v.key}>
                    {v.pkg.name} ({v.vendor}, {inr(v.pkg.price)} {v.pkg.priceUnit})
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <TextField
                select label="Ceremony Type" value={form.ceremonyType}
                onChange={e => handleTypeSelect(e.target.value)} fullWidth required>
                {templates.map(t => <MenuItem key={t.ceremonyType} value={t.ceremonyType}>{t.displayName}</MenuItem>)}
              </TextField>
            )}
            <TextField label="Name / Title" value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))} fullWidth required />
            <TextField label="Event Date" type="date" value={form.eventDate}
              onChange={e => setForm(f => ({ ...f, eventDate: e.target.value }))} fullWidth InputLabelProps={{ shrink: true }} />
            <TextField label="Venue" value={form.venue}
              onChange={e => setForm(f => ({ ...f, venue: e.target.value }))} fullWidth />
            <TextField label="Notes" value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} fullWidth multiline rows={3} />
            {useVendor && chosen && (
              <Alert severity="info" icon={<CheckIcon />}>
                {chosen.pkg.items.length} ritual items from {chosen.vendor} ({inr(chosen.pkg.price)}) go on this ceremony's checklist
                {chosen.pkg.ceremonyType && templates.find(t => t.ceremonyType === chosen.pkg.ceremonyType)
                  ? `, with the ${templates.find(t => t.ceremonyType === chosen.pkg.ceremonyType).defaultTasks?.length || 0} tasks of the standard ceremony.` : '.'}
              </Alert>
            )}
            {!useVendor && form.ceremonyType && templates.find(t => t.ceremonyType === form.ceremonyType) && (
              <Alert severity="info" icon={<CheckIcon />}>
                {templates.find(t => t.ceremonyType === form.ceremonyType)?.defaultTasks?.length || 0} default tasks will be auto-created
              </Alert>
            )}
            {fromPkgError && <Alert severity="error">{fromPkgError.message}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate}
            disabled={useVendor ? !chosen : (!form.name || !form.ceremonyType)}>Create</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Ceremony</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Name / Title" value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))} fullWidth required />
            <TextField label="Event Date" type="date" value={form.eventDate}
              onChange={e => setForm(f => ({ ...f, eventDate: e.target.value }))} fullWidth InputLabelProps={{ shrink: true }} />
            <TextField label="Venue" value={form.venue}
              onChange={e => setForm(f => ({ ...f, venue: e.target.value }))} fullWidth />
            <TextField select label="Status" value={form.status}
              onChange={e => setForm(f => ({ ...f, status: e.target.value }))} fullWidth>
              {statusOptions.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
            </TextField>
            <TextField label="Notes" value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} fullWidth multiline rows={3} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained"
            onClick={() => updateCeremony({ variables: { id: editId, name: form.name, eventDate: form.eventDate || null, venue: form.venue || null, notes: form.notes || null, status: form.status } })}
            disabled={!form.name}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
